// Pure helpers for the Payments panel: money in minor units, labels, and the
// go-live checklist summary. No React/store imports so it stays unit-testable.
import type { PaymentMode, PaymentStatus } from '@dunea/shared';
import type { PaymentAccount, PaymentListRow, PaymentsChecklistItem, PaymentsLiveState } from '~/lib/api/payments';

export type Tone = 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

// ─── money ───

/** Digits after the decimal point for a currency (JPY 0, USD 2, KWD 3). Unknown codes → 2. */
export function currencyExponent(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** Format integer minor units (paise, cents) for display, e.g. 129900 INR → "₹1,299.00". */
export function formatMinor(amount: number, currency: string, locale?: string): string {
  const exp = currencyExponent(currency);
  const major = amount / 10 ** exp;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: exp,
      maximumFractionDigits: exp,
    }).format(major);
  } catch {
    return `${major.toFixed(exp)} ${currency}`;
  }
}

/**
 * Parse an owner-typed major amount ("12", "12.5", "1,299.00") into minor units
 * without float rounding. Returns null for anything that isn't a positive
 * amount with at most the currency's number of decimals.
 */
export function majorToMinor(input: string, currency: string): number | null {
  const exp = currencyExponent(currency);
  const clean = input.trim().replace(/,/g, '');
  const match = /^(\d+)(?:\.(\d*))?$/.exec(clean);

  if (!match) {
    return null;
  }

  const [, whole, frac = ''] = match;

  if (frac.length > exp) {
    return null;
  }

  const minor = Number(whole) * 10 ** exp + Number(frac.padEnd(exp, '0') || '0');

  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

/** Minor units back to a plain major string for an input's default value ("1299.00"). */
export function minorToMajorInput(amount: number, currency: string): string {
  const exp = currencyExponent(currency);
  return (amount / 10 ** exp).toFixed(exp);
}

// ─── labels ───

const PURPOSE_LABELS: Record<string, string> = {
  'shop.order': 'Shop order',
  'funnel.offer': 'Funnel',
  'payments.link': 'Buy button',
};

export function purposeLabel(purpose: string): string {
  return PURPOSE_LABELS[purpose] ?? purpose;
}

const PROVIDER_LABELS: Record<string, string> = {
  stripe: 'Stripe',
  razorpay: 'Razorpay',
  upi: 'UPI',
  cod: 'Cash on delivery',
};

/** Providers whose payments the owner confirms by hand (no webhook tells us). */
const MANUAL_PROVIDERS = new Set(['cod', 'upi']);

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

export function liveStateMeta(state: PaymentsLiveState): { label: string; tone: Tone } {
  switch (state) {
    case 'live':
      return { label: 'Live', tone: 'accent' };
    case 'paused':
      return { label: 'Paused', tone: 'warning' };
    default:
      return { label: 'Test', tone: 'info' };
  }
}

const STATUS_META: Record<PaymentStatus, { label: string; tone: Tone }> = {
  pending: { label: 'Pending', tone: 'warning' },
  paid: { label: 'Paid', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  expired: { label: 'Expired', tone: 'muted' },
  refunded: { label: 'Refunded', tone: 'muted' },
  partially_refunded: { label: 'Partly refunded', tone: 'info' },
  disputed: { label: 'Disputed', tone: 'danger' },
};

export function paymentStatusMeta(status: string): { label: string; tone: Tone } {
  return STATUS_META[status as PaymentStatus] ?? { label: status, tone: 'muted' };
}

export function recurringLabel(recurring: { interval: string; intervalCount?: number } | null | undefined): string {
  if (!recurring) {
    return 'One-time';
  }

  const n = recurring.intervalCount ?? 1;

  if (n === 1) {
    return { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' }[recurring.interval] ?? recurring.interval;
  }

  return `Every ${n} ${recurring.interval}s`;
}

// ─── accounts ───

export type AccountState = 'none' | 'pending' | 'active' | 'restricted';

export function findAccount(
  accounts: PaymentAccount[],
  provider: string,
  mode: PaymentMode,
): PaymentAccount | undefined {
  return accounts.find((a) => a.provider === provider && a.mode === mode);
}

export function accountState(account: PaymentAccount | undefined): AccountState {
  return account?.status ?? 'none';
}

export function accountStateMeta(state: AccountState): { label: string; tone: Tone } {
  switch (state) {
    case 'active':
      return { label: 'Active', tone: 'success' };
    case 'pending':
      return { label: 'Onboarding pending', tone: 'warning' };
    case 'restricted':
      return { label: 'Restricted', tone: 'danger' };
    default:
      return { label: 'Not connected', tone: 'muted' };
  }
}

// ─── checklist ───

export type ChecklistState = 'ok' | 'blocking' | 'advisory';

/**
 * Blocking items are ✓ or ✗. Non-blocking items are reminders the server can't
 * verify (e.g. policy pages), so they always render as advisory "!".
 */
export function checklistItemState(item: PaymentsChecklistItem): ChecklistState {
  if (!item.blocking) {
    return 'advisory';
  }

  return item.ok ? 'ok' : 'blocking';
}

export function summarizeChecklist(items: PaymentsChecklistItem[]): {
  canGoLive: boolean;
  blockingLeft: number;
  advisories: number;
} {
  const blockingLeft = items.filter((i) => i.blocking && !i.ok).length;
  const advisories = items.filter((i) => !i.blocking).length;

  return { canGoLive: items.length > 0 && blockingLeft === 0, blockingLeft, advisories };
}

// ─── payment rows ───

export function refundableAmount(row: Pick<PaymentListRow, 'amount' | 'refundedAmount'>): number {
  return Math.max(0, row.amount - (row.refundedAmount ?? 0));
}

export function canRefund(row: Pick<PaymentListRow, 'status' | 'amount' | 'refundedAmount'>): boolean {
  return (row.status === 'paid' || row.status === 'partially_refunded') && refundableAmount(row) > 0;
}

export function canMarkPaid(row: Pick<PaymentListRow, 'status' | 'provider'>): boolean {
  return MANUAL_PROVIDERS.has(row.provider) && row.status === 'pending';
}

/** Pending manual payments can be rejected: cash never collected, or no UPI transfer for the UTR. */
export function canReject(row: Pick<PaymentListRow, 'status' | 'provider'>): boolean {
  return canMarkPaid(row);
}

/**
 * A UPI payment waiting on the owner: the buyer submitted proof (a UTR, a
 * screenshot, or both). Without it the buyer hasn't said they paid yet, and
 * the checkout lapses on its own.
 */
export function awaitingUpiCheck(
  row: Pick<PaymentListRow, 'status' | 'provider' | 'buyerReference' | 'hasScreenshot'>,
): boolean {
  return row.provider === 'upi' && row.status === 'pending' && Boolean(row.buyerReference || row.hasScreenshot);
}

/** UPI ID shape check before saving (the server normalizes and checks again). */
export function isValidUpiId(input: string): boolean {
  return /^[a-z0-9][a-z0-9._-]{1,255}@[a-z][a-z0-9.-]{1,63}$/.test(input.trim().toLowerCase());
}

/** Best available timestamp for a row: created, else paid. */
export function paymentDate(row: Pick<PaymentListRow, 'createdAt' | 'paidAt'>): string | null {
  return row.createdAt ?? row.paidAt ?? null;
}

// ─── offers ───

export const OFFER_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

/** "Coaching call!" → "coaching-call" — a starting point for the slug field. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function isValidCurrency(code: string): boolean {
  return /^[A-Za-z]{3}$/.test(code.trim());
}

// ─── errors ───

/** The API's user-facing message (Nest puts it at `response.data.message`), else the fallback. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  return typeof message === 'string' && message.trim() ? message : fallback;
}
